import React, { useContext } from 'react';

import { t } from 'i18next';

import BdsTooltip from '../../../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import { CompanyContext } from '../../../../../../../shared/context/company/companyContext';
import { ICoverageDate } from '../../../../../../../shared/interfaces/coverageGraph';
import { IMetricsProtectionCoverage } from '../../../../../../../shared/interfaces/metrics';
import { TRANSLATIONS_METRICS_NE } from '../../../../../../../shared/translations/metrics';
import { formatToLocaleMonthDay } from '../../../../../../../shared/utils/datetime/datetime';
import { toLocaleNumberFormat } from '../../../../../../../shared/utils/utils';
import './CoverageGraphSection.scss';

const STEP_MIN_HEIGHT = 141;
const STEP_MAX_HEIGHT = 421;

interface ICoverageGraphData {
  tooltipMessage?: string;
  isEmpty?: boolean;
  coverageData?: IMetricsProtectionCoverage;
  setHoveredIndex?: (index: number | null) => void;
  hoveredIndex?: number | null;
}

const CoverageGraphSection = ({
  coverageData,
  hoveredIndex,
  isEmpty,
  setHoveredIndex,
  tooltipMessage
}: ICoverageGraphData) => {
  const coverageGraphStepRender = (
    stepData: ICoverageDate,
    height: number,
    index: number,
    totalSteps: number,
    isLast: boolean
  ) => {
    const isHovered = index === hoveredIndex;
    const colorIndex = totalSteps === 1 ? 5 : 5 - (totalSteps - 1 - index);
    const classEmpty = isEmpty ? 'no-data' : '';
    const classSingleElement = totalSteps === 1 ? 'single-element' : '';
    const classEqulasElement = isLast ? 'equals' : '';
    const classHovered = !isHovered && hoveredIndex !== null && 'no-hovered';
    const tooltipClassNames = 'tooltip-hover';
    const width = totalSteps === 0 ? '100%' : `${100 / totalSteps}%`;

    const stepClassNames = [
      'ctg-step flex flex-col justify-end items-start',
      classEmpty,
      classSingleElement,
      classEqulasElement,
      classHovered,
      `step-${colorIndex}`
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="step-hover-container" key={index} style={{ width }}>
        {tooltipMessage && (
          <BdsTooltip message={t(tooltipMessage)} position="top-center" variant="standard" open={isHovered}>
            <div className={tooltipClassNames}></div>
          </BdsTooltip>
        )}
        <button
          className={stepClassNames}
          style={{ height: `${height}px` }}
          onMouseEnter={() => setHoveredIndex?.(index)}
          onMouseLeave={() => setHoveredIndex?.(null)}
          aria-label={`Step ${index + 1}`}
        >
          <span className="value">{`+${toLocaleNumberFormat(stepData?.earnedAmount ?? 0)} ${
            coverageData?.currency
          }`}</span>
          <span className="date">
            {stepData && formatToLocaleMonthDay(stepData.startDate, locale)} -{' '}
            {formatToLocaleMonthDay(stepData.endDate, locale)}
          </span>
        </button>
      </div>
    );
  };

  const { locale } = useContext(CompanyContext);

  const coverageList = coverageData?.periods ?? [];
  const totalEarnedCoverage = coverageList.reduce((acc, step) => acc + (step.earnedAmount ?? 0), 0);
  let accumulatedCoverage = 0;
  let hoverPosition = '';
  if (coverageList.length === 2) {
    hoverPosition = hoveredIndex === 0 ? 'right' : 'left';
  } else {
    hoverPosition = hoveredIndex === 0 || hoveredIndex === 1 ? 'right' : 'left';
  }
  const bottomClassNames = [
    'bottom-ctg',
    isEmpty && 'no-data',
    hoveredIndex != null && 'hovered',
    !isEmpty && 'no-empty',
    hoverPosition
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="ct-graph flex flex-col justify-end">
      <div
        className={`ctg-steps-container flex flex-row items-end justify-center ${
          coverageList.length === 1 ? 'single-element' : ''
        }`}
      >
        {coverageList.map((step, index) => {
          accumulatedCoverage += step.earnedAmount ?? 0;
          const heightProportion = totalEarnedCoverage ? accumulatedCoverage / totalEarnedCoverage : 0;
          const height = heightProportion * (STEP_MAX_HEIGHT - STEP_MIN_HEIGHT) + STEP_MIN_HEIGHT;
          return coverageGraphStepRender(step, height, index, coverageList.length, index === coverageList.length - 1);
        })}
        {hoveredIndex !== null && (
          <button
            className={bottomClassNames}
            onMouseEnter={() => setHoveredIndex?.(hoveredIndex ?? null)}
            onMouseLeave={() => setHoveredIndex?.(null)}
          >
            <h4>{t(TRANSLATIONS_METRICS_NE.protections.coverageGraph.textBottom)}</h4>
          </button>
        )}
      </div>
    </div>
  );
};

export default CoverageGraphSection;
